import React, { Component, createRef } from 'react'
import { useSelector } from 'react-redux'
import { nativeSetValue } from 'utils'
import { criteriaOptions } from 'common/criteria'
import { T } from 'components/Translations'
import Icon from 'components/Icon'
import { Input } from 'components/Controls'
import StyledSelect, { SelectOption } from './StyledSelect'

const transformForSelect = (arr = [], text = 'name', value = 'id') =>
  arr.map(item => ({ value: item[value].toString(), text: item[text] }))

class Select extends Component {
  state = this.initialState();
  select = createRef();
  input = createRef();
  header = createRef();
  filter = createRef();

  initialState({ selected = [], options = [] } = this.props) {
    if (options.length && !['text', 'value'].every(key => key in options[0])) {
      options = transformForSelect(options, this.props.textField)
    }
    if (Array.isArray(selected)) {
      selected = selected.filter(val => val).map(val => val.toString())
    } else {
      selected = selected ? [selected] : [options?.[0]?.value.toString()]
    }
    return { options, selected }
  }

  componentDidUpdate(prevProps) {
    JSON.stringify(prevProps) !== JSON.stringify(this.props) && this.setState({ ...this.initialState() })
    if (this.props.selected?.toString() !== prevProps.selected?.toString()) {
      this.input.current.value = this.props.selected || ''
    }
  }

  onClick = e => {
    e.preventDefault()
    this.filter.current?.focus({ preventScroll: true })
  };

  onHeaderClick = () => this.setState({ open: !this.state.open, filter: '' });

  onOptionClick = e => {
    const { multiSelect, empty } = this.props
    let { selected } = this.state
    const value = e.currentTarget.getAttribute('value')
    const remove = selected.includes(value)
    selected = !multiSelect ? [value] : remove ? selected.filter(v => v != value) : [...selected, value].sort()
    !empty && selected.length == 0 && (selected = [value])

    nativeSetValue(this.input.current, selected)
    this.setState({ selected, open: multiSelect })
  };

  onAllOptionsClick = () => {
    nativeSetValue(this.input.current, '')
    this.setState({ selected: [] })
  };

  onBlur = e => this.state.open && !this.select.current?.contains(e.target) && this.setState({ open: false });

  removeSelectedValue = e => {
    this.state.open && e.stopPropagation() //stop onHeader click event when clicking chip
    this.onOptionClick(e)
  };

  renderChip = ({ text, value }) => (
    <span className="ui-option" key={value}>
      {text}
      <div value={value} onClick={this.removeSelectedValue} className="fa fa-times"></div>
    </span>
  );

  renderSelected() {
    const { multiSelect, empty } = this.props
    const { options, selected } = this.state

    const active = options.filter(o => o.text && selected.includes('' + o.value))
    if (!selected.length && empty && empty !== true) {
      // display only custom empty value
      return <T as="span" className="ui-option" label={empty} />
    }

    return multiSelect ? active.map(this.renderChip) : active.map(o => o.text).join(', ')
  }

  componentDidMount() {
    window.selectBlurs.push(this.onBlur)
  }
  componentWillUnmount() {
    window.selectBlurs = window.selectBlurs.filter(onBlur => onBlur != this.onBlur)
  }

  renderOptions(options) {
    const { multiSelect } = this.props
    const { selected } = this.state

    return options.map(({ value, text }) => (
      <SelectOption key={value} value={value} selected={selected.includes(value)} onClick={this.onOptionClick}>
        {multiSelect && <i className={`fa fa${selected.includes(value) ? '-check' : ''}-square`} />}
        <span className="option__text">{text}</span>
      </SelectOption>
    ))
  }

  hasProp = key => Object.prototype.hasOwnProperty.call(this.props, key);
  getValue = (key, fallback = this.state[key]) => (this.hasProp(key) ? this.props[key] : fallback);

  render() {
    const { name, multiSelect, filterable, onChange, empty } = this.props
    const { selected, open, options } = this.state
    const disabled = this.getValue('disabled', !options.length)
    const filter = this.getValue('filter')
    const filtered = !filter ? options : options.filter(o => o.text.toLowerCase().includes(filter.toLowerCase()))
    const hasFilter = filterable || (multiSelect && options.length > 5)
    const setFilter = this.getValue('setFilter', filter => this.setState({ filter }))

    return (
      <StyledSelect
        ref={this.select}
        disabled={disabled}
        onClick={this.onClick}
        active={open}
        multiSelect={multiSelect}
        className={`${open ? 'open' : 'closed'} ${selected.length > 3 ? 'long' : 'short'} ${
          options.length ? 'has' : 'no'
        }-options`}
      >
        <div className="header" onClick={this.onHeaderClick}>
          <div ref={this.header} className="header__text">
            {this.renderSelected()}
          </div>
          <input onChange={onChange} name={name} defaultValue={selected} ref={this.input} />
          <Icon name="angle-down" />
        </div>
        {hasFilter && (
          <Input
            name="select"
            innerRef={this.filter}
            placeholder="Type to filter"
            onChange={e => setFilter(e.target.value)}
            className={`filter ${open ? '' : 'hidden'}`}
            value={filter || ''}
            autoComplete="off"
          />
        )}
        <div className="options">
          {empty && empty !== true && !filter && (
            <SelectOption selected={!selected.length} onClick={this.onAllOptionsClick}>
              <i className={`fa fa${!selected.length ? '-check' : ''}-square`} />
              <T as="span" className="option__text" label={empty} />
            </SelectOption>
          )}
          {filtered.length ? (
            this.renderOptions(filtered)
          ) : (
            <SelectOption className="reset" onClick={() => setFilter('')}>
              No matches(reset)
            </SelectOption>
          )}
        </div>
      </StyledSelect>
    )
  }
}

export const CriteriaSelect = ({ id, group, ...props }) => (
  <Select
    name={`${group}[${id}]`}
    options={criteriaOptions(
      id,
      useSelector(s => s.registry)
    )}
    {...props}
  />
)

export default Select
